import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import GameInfoPopup from '../../../components/GameInfoPopup/GameInfoPopup';
import StageHud from '../../../components/StageHud/StageHud';
import useTouchDragDrop from '../../../hooks/useTouchDragDrop';
import { GAME_COPY, GAME_KEYS, STAGE3_DRAGS, STAGE3_DROPS } from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import { useCarnivalTimer } from '../../../hooks/useCarnivalTimer';
import { useStagePoints } from '../../../hooks/useStagePoints';
import './GenderQuestPage.css';

export default function GenderQuestPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const copy = GAME_COPY.stage3;

  const [placed, setPlaced] = useState(() => ({}));
  const [hiddenDrags, setHiddenDrags] = useState(() => new Set());
  const [score, setScore] = useState(0);
  const [busy, setBusy] = useState(false);
  const [popupSrc, setPopupSrc] = useState(null);
  const [selectedDragPos, setSelectedDragPos] = useState(null);
  const doneRef = useRef(false);
  const dragPosRef = useRef(null);
  const scoreRef = useRef(0);
  const placedRef = useRef(placed);
  placedRef.current = placed;
  const suppressClickRef = useRef(false);

  const { displayPoints, alreadyPlayed, markPlayedAndSyncCoins } = useStagePoints(GAME_KEYS.stage3, score);
  const { display: timerDisplay, elapsedRef } = useCarnivalTimer({ running: !alreadyPlayed });

  const tryPlaceAt = useCallback(
    (dropPos, dragPos) => {
      if (dragPos == null || placedRef.current[dropPos] || busy || doneRef.current) return;
      if (dragPos !== dropPos) return;

      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 400);

      const drop = STAGE3_DROPS[dropPos];
      setPlaced((p) => ({ ...p, [dropPos]: drop.answer }));
      setHiddenDrags((prev) => new Set(prev).add(dragPos));
      scoreRef.current += 1;
      setScore(scoreRef.current);
      setPopupSrc(drop.popup);
      setSelectedDragPos(null);
      dragPosRef.current = null;
    },
    [busy]
  );

  const dragEnabled = !popupSrc && !busy && !doneRef.current;

  const { allowHtml5Drag, ghost, hoverDropPos, startPointerPick } = useTouchDragDrop({
    onDrop: tryPlaceAt,
    enabled: dragEnabled,
  });

  const onDragStart = (pos) => {
    dragPosRef.current = pos;
  };

  const onHtmlDrop = (dropPos) => {
    tryPlaceAt(dropPos, dragPosRef.current);
  };

  const onChipClick = (pos) => {
    if (suppressClickRef.current || hiddenDrags.has(pos)) return;
    setSelectedDragPos((prev) => (prev === pos ? null : pos));
  };

  const onDropZoneClick = (dropPos) => {
    if (suppressClickRef.current || placed[dropPos]) return;
    if (selectedDragPos != null) {
      tryPlaceAt(dropPos, selectedDragPos);
      return;
    }
    if (dragPosRef.current != null) {
      tryPlaceAt(dropPos, dragPosRef.current);
    }
  };

  const onPopupNext = () => {
    setPopupSrc(null);
    if (scoreRef.current >= STAGE3_DROPS.length) {
      submit(scoreRef.current);
    }
  };

  const submit = async (finalScore = scoreRef.current) => {
    if (doneRef.current || busy || !user) return;
    doneRef.current = true;
    setBusy(true);
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.stage3,
        score: finalScore,
        elapsedSeconds: elapsedRef.current,
      });
      markPlayedAndSyncCoins(result);
      if (result.alreadyPlayed) navigate('/hub');
      else navigate(`/thank-you?game=${GAME_KEYS.stage3}&points=${result.coinsEarned ?? 0}`);
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      setBusy(false);
      Swal.fire({
        title: 'Could not save score',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
    }
  };


  return (
    <div
      className="gender-page stage-bg"
      style={{
        '--ic-bg': 'url(/img/BG_2.jpg)',
        '--ic-art': 'url(/img/intro.jpg)',
        '--ic-art-mob': 'url(/stage3/img/mobile.png)',
      }}
    >
      <div className="gender-stage-art" aria-hidden="true" />
      <div className="gender-alldata">
        <StageHud
          title={copy.title}
          instruction={copy.instruction}
          timerDisplay={timerDisplay}
          points={displayPoints}
          rules={copy.rules}
        />

        <div className="dropBox">
          {STAGE3_DROPS.map((drop) => (
            <div key={drop.pos} className="drop-item">
              <img src={drop.img} alt="" className="drop-flag" draggable={false} />
              <div
                className={`dropContainer${hoverDropPos === drop.pos ? ' drop-hover' : ''}${
                  placed[drop.pos] ? ' drop-filled' : ''
                }`}
                data-drop-pos={drop.pos}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onHtmlDrop(drop.pos);
                }}
                onClick={() => onDropZoneClick(drop.pos)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onDropZoneClick(drop.pos);
                }}
              >
                {placed[drop.pos] || ''}
              </div>
            </div>
          ))}
        </div>

        <div className="dropButtonBox">
          {STAGE3_DRAGS.map((drag) =>
            hiddenDrags.has(drag.pos) ? null : (
              <div
                key={drag.pos}
                className={`dropbutton${selectedDragPos === drag.pos ? ' dropbutton-selected' : ''}`}
                draggable={allowHtml5Drag}
                onDragStart={(e) => {
                  onDragStart(drag.pos);
                  e.dataTransfer.setData('text/plain', String(drag.pos));
                }}
                onDragEnd={() => {
                  dragPosRef.current = null;
                }}
                onPointerDown={(e) =>
                  startPointerPick(e, { pos: drag.pos, label: drag.label })
                }
                onClick={() => onChipClick(drag.pos)}
              >
                {drag.label}
              </div>
            )
          )}
        </div>
      </div>

      {ghost.visible && (
        <div
          className="gender-drag-ghost"
          style={{ left: ghost.x, top: ghost.y }}
          aria-hidden
        >
          {ghost.label}
        </div>
      )}

      {popupSrc && (
        <GameInfoPopup
          imageSrc={popupSrc}
          buttonLabel="Next"
          onNext={onPopupNext}
        />
      )}
    </div>
  );
}
